import { useEffect } from 'react';
import { useBoolean } from 'ahooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Drawer, Button, message, Input, Switch } from 'antd';
import { get } from 'lodash';
import { createRoleApi, updateRoleApi } from '@/services/roles';
import { collapseAliasPermissions } from '@/utils';

/**
 * Types
 */
import type { IntlShape } from 'react-intl';

export type ModalFormData = {
    id: string;
    name: string;
    status: 'Enabled' | 'Disabled';
    permissions: string[] | null;
    updated_at?: string;
};

export type ModalFormState = {
    open: boolean;
    data: ModalFormData | undefined;
};

type Props = {
    state: ModalFormState;
    onClose: (reload: boolean) => void;
};

const RoleModalForm: React.FC<Props> = ({ state, onClose }) => {
    /**
     * State
     */
    const [submitting, { setFalse: setSubmittingFlase, setTrue: setSubmittingTrue }] = useBoolean(false);
    const [form] = Form.useForm();
    const intl: IntlShape = useIntl();

    /**
     * Effect
     */
    useEffect(() => {
        if (!state.open) {
            form.setFieldsValue({
                name: '',
                status: true,
            });
            setSubmittingFlase();
        } else if (state.data) {
            form.setFieldsValue({
                name: state.data.name,
                status: state.data.status === 'Enabled',
            });
        }
    }, [state]);

    const handleFinish = async (formData: any) => {
        const status: 'Enabled' | 'Disabled' = formData.status ? 'Enabled' : 'Disabled';
        setSubmittingTrue();
        if (state?.data?.id) {
            const res = await updateRoleApi(state.data.id, {
                name: formData.name,
                status,
                permissions: collapseAliasPermissions(state.data.permissions || []),
            });
            if (get(res, 'data.code') === 200) {
                message.success(intl.formatMessage({ id: 'common.submitSuccess' }));
                onClose(true);
            } else {
                message.error(get(res, 'data.msg', intl.formatMessage({ id: 'common.undefinedError' })));
            }
        } else {
            const res = await createRoleApi({
                name: formData.name,
                status,
                permissions: [],
            });
            if (get(res, 'data.code') === 200) {
                message.success(intl.formatMessage({ id: 'common.submitSuccess' }));
                onClose(true);
            } else {
                message.error(get(res, 'data.msg', intl.formatMessage({ id: 'common.undefinedError' })));
            }
        }
        setSubmittingFlase();
    };

    const drawerProps = {
        title: state.data?.id ? <FormattedMessage id="b.edit" /> : <FormattedMessage id="b.add" />,
        open: state.open,
        maskClosable: false,
        destroyOnClose: true,
        onClose: () => onClose(false),
        footer: (
            <div style={{ textAlign: 'right' }}>
                <Button onClick={() => onClose(false)} style={{ marginRight: 8 }}>
                    {intl.formatMessage({ id: 'common.cancel' })}
                </Button>
                <Button onClick={() => form.submit()} type="primary" loading={submitting}>
                    {intl.formatMessage({ id: 'common.sure' })}
                </Button>
            </div>
        ),
    };

    return (
        <Drawer {...drawerProps}>
            <Form form={form} layout="vertical" labelAlign="right" onFinish={handleFinish}>
                <Form.Item
                    name="name"
                    label={<FormattedMessage id="common.role" />}
                    rules={[{ required: true, message: intl.formatMessage({ id: 'notEmpty' }) }]}
                >
                    <Input
                        placeholder={intl.formatMessage({ id: 'p.rm.ph.name' })}
                        showCount
                        maxLength={20}
                        autoComplete="off"
                    />
                </Form.Item>
                <Form.Item name="status" label={<FormattedMessage id="common.status" />} valuePropName="checked">
                    <Switch />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default RoleModalForm;
